import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart3,
  Users,
  Tag,
  Loader2,
  RefreshCw,
  ChevronDown,
  Filter,
  CheckSquare,
  Square,
  Phone,
} from "lucide-react";
import api from "../services/api";
import { getAgentAnalysis, getAvailableAgents, getStatuses } from "../apis/livechat";
import { toast } from "react-toastify";

/**
 * Flatten WhatsApp accounts to list of { phoneId, phoneNumber, phoneName }
 */
function flattenPhoneAccounts(data) {
  if (!data?.success || !Array.isArray(data.data)) return [];
  const flattened = [];
  data.data.forEach((account) => {
    if (account.phone_numbers && Array.isArray(account.phone_numbers) && account.phone_numbers.length > 0) {
      account.phone_numbers.forEach((phone) => {
        flattened.push({
          phoneId: phone.phone_id,
          phoneNumber: phone.phone_number,
          phoneName: phone.phone_name || phone.verified_name || account.account_name || "WhatsApp",
        });
      });
    } else if (account.phone_id) {
      flattened.push({
        phoneId: account.phone_id,
        phoneNumber: account.phone_number,
        phoneName: account.verified_name || account.phone_name || account.account_name || "WhatsApp",
      });
    }
  });
  return flattened;
}

/**
 * Agent Analysis - Super admin only. Filters: agents, statuses, date range, phone number.
 * Shows total chats, count by status, count by agent.
 */
const AgentAnalysis = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState([]);
  const [agents, setAgents] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [filters, setFilters] = useState({
    agent_ids: [],
    status_ids: [],
    date_from: "",
    date_to: "",
    phone_id: "",
  });
  const [showAgentFilter, setShowAgentFilter] = useState(false);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [showPhoneFilter, setShowPhoneFilter] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);

  const loadAgents = useCallback(() => {
    setLoadingAgents(true);
    getAvailableAgents()
      .then((res) => {
        if (res.data?.success) setAgents(res.data.data || []);
      })
      .catch(() => toast.error("Failed to load agents"))
      .finally(() => setLoadingAgents(false));
  }, []);

  const loadStatuses = useCallback(() => {
    getStatuses()
      .then((res) => {
        if (res.data?.success) setStatuses(res.data.data || []);
      })
      .catch(() => {});
  }, []);

  const loadPhones = useCallback(() => {
    api
      .get("/api/whatsapp-accounts")
      .then((res) => setPhoneNumbers(flattenPhoneAccounts(res.data)))
      .catch(() => toast.error("Failed to load phone numbers"));
  }, []);

  const loadAnalysis = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAgentAnalysis({
        agent_ids: filters.agent_ids.length ? filters.agent_ids : undefined,
        status_ids: filters.status_ids.length ? filters.status_ids : undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        phone_id: filters.phone_id || undefined,
      });
      if (res.data?.success) setData(res.data.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load analysis");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadStatuses();
    loadAgents();
    loadPhones();
  }, [loadStatuses, loadAgents, loadPhones]);

  useEffect(() => {
    loadAnalysis();
  }, [loadAnalysis]);

  const toggleAgent = (id) => {
    setFilters((f) => ({
      ...f,
      agent_ids: f.agent_ids.includes(id) ? f.agent_ids.filter((x) => x !== id) : [...f.agent_ids, id],
    }));
  };

  const toggleStatus = (id) => {
    setFilters((f) => ({
      ...f,
      status_ids: f.status_ids.includes(id) ? f.status_ids.filter((x) => x !== id) : [...f.status_ids, id],
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 size={24} className="text-primary" />
              Agent Analysis
            </h1>
            <button
              type="button"
              onClick={loadAnalysis}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <div className="relative">
              <button
                type="button"
                onClick={() => { setShowAgentFilter(!showAgentFilter); setShowStatusFilter(false); loadAgents(); }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border border-gray-200 bg-white hover:bg-gray-50"
              >
                <Filter size={14} />
                Agents {filters.agent_ids.length > 0 ? `(${filters.agent_ids.length})` : ""}
                <ChevronDown size={14} />
              </button>
              {showAgentFilter && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowAgentFilter(false)} aria-hidden="true" />
                  <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto min-w-[200px]">
                    {loadingAgents ? (
                      <div className="p-4 flex justify-center"><Loader2 size={20} className="animate-spin" /></div>
                    ) : (
                      agents.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => toggleAgent(a.id)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        >
                          {filters.agent_ids.includes(a.id) ? <CheckSquare size={16} className="text-primary" /> : <Square size={16} className="text-gray-400" />}
                          {a.name}
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => { setShowStatusFilter(!showStatusFilter); setShowAgentFilter(false); }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border border-gray-200 bg-white hover:bg-gray-50"
              >
                <Filter size={14} />
                Status {filters.status_ids.length > 0 ? `(${filters.status_ids.length})` : ""}
                <ChevronDown size={14} />
              </button>
              {showStatusFilter && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowStatusFilter(false)} aria-hidden="true" />
                  <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto min-w-[180px]">
                    {statuses.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleStatus(s.id)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                      >
                        {filters.status_ids.includes(s.id) ? <CheckSquare size={16} className="text-primary" /> : <Square size={16} className="text-gray-400" />}
                        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color || "#9ca3af" }} />
                        {s.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => { setShowPhoneFilter(!showPhoneFilter); setShowAgentFilter(false); setShowStatusFilter(false); }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm border border-gray-200 bg-white hover:bg-gray-50"
              >
                <Phone size={14} />
                {filters.phone_id
                  ? (phoneNumbers.find((p) => p.phoneId === filters.phone_id)?.phoneName || filters.phone_id)
                  : "All phones"}
                <ChevronDown size={14} />
              </button>
              {showPhoneFilter && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowPhoneFilter(false)} aria-hidden="true" />
                  <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto min-w-[220px]">
                    <button
                      type="button"
                      onClick={() => { setFilters((f) => ({ ...f, phone_id: "" })); setShowPhoneFilter(false); }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100"
                    >
                      {!filters.phone_id ? <CheckSquare size={16} className="text-primary" /> : <Square size={16} className="text-gray-400" />}
                      All phones
                    </button>
                    {phoneNumbers.map((p) => (
                      <button
                        key={p.phoneId}
                        type="button"
                        onClick={() => { setFilters((f) => ({ ...f, phone_id: p.phoneId })); setShowPhoneFilter(false); }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                      >
                        {filters.phone_id === p.phoneId ? <CheckSquare size={16} className="text-primary flex-shrink-0" /> : <Square size={16} className="text-gray-400 flex-shrink-0" />}
                        <span className="truncate min-w-0">
                          {p.phoneName || p.phoneNumber || p.phoneId}
                          {p.phoneNumber && p.phoneName ? ` • ${p.phoneNumber}` : ""}
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
              title="From date"
            />
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg"
              title="To date"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={40} className="animate-spin text-primary" />
          </div>
        ) : data ? (
          <div className="space-y-6">
            {/* Summary card: total, allocated, unallocated */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-700 mb-4">Summary</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium">Total chats</p>
                  <p className="text-2xl font-bold text-gray-900">{data.total_chats ?? 0}</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600 font-medium">Allocated</p>
                  <p className="text-2xl font-bold text-blue-900">{data.allocated_chats ?? 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Assigned to agents</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg">
                  <p className="text-xs text-amber-700 font-medium">Unallocated</p>
                  <p className="text-2xl font-bold text-amber-900">{data.unallocated_chats ?? 0}</p>
                  <p className="text-xs text-gray-500 mt-1">No agent assigned</p>
                </div>
              </div>
            </div>

            {/* By status */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <h2 className="text-sm font-semibold text-gray-700 p-4 border-b border-gray-100 flex items-center gap-2">
                <Tag size={18} />
                By status
              </h2>
              {data.by_status && data.by_status.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        <th className="px-4 py-3 font-medium text-gray-600">Status</th>
                        <th className="px-4 py-3 font-medium text-gray-600">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_status.map((row) => (
                        <tr key={row.status_id ?? "null"} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span
                              className="inline-flex items-center gap-2 px-2 py-1 rounded font-medium text-xs"
                              style={{
                                backgroundColor: (row.color || "#9ca3af") + "20",
                                color: row.color || "#6b7280",
                              }}
                            >
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: row.color || "#9ca3af" }} />
                              {row.name || "No status"}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900">{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500 text-sm">No status breakdown for the selected filters.</div>
              )}
            </div>

            {/* By agent: which agent was allocated which chats */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <h2 className="text-sm font-semibold text-gray-700 p-4 border-b border-gray-100 flex items-center gap-2">
                <Users size={18} />
                By agent (chats allocated to each agent)
              </h2>
              {data.by_agent && data.by_agent.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        <th className="px-4 py-3 font-medium text-gray-600">Agent</th>
                        <th className="px-4 py-3 font-medium text-gray-600">Email</th>
                        <th className="px-4 py-3 font-medium text-gray-600">Assigned chats</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_agent.map((row) => (
                        <tr key={row.agent_id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{row.name}</td>
                          <td className="px-4 py-3 text-gray-600">{row.email}</td>
                          <td className="px-4 py-3 font-medium text-gray-900">{row.assigned_chats_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500 text-sm">No agents with live chat access, or no data for the selected filters.</div>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-500">
            Could not load analysis.
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentAnalysis;
